import { z } from 'zod';
import { PURPOSES } from '../types/link';
import { SLUG_LENGTH } from './slug';

// Client-side validation for the Create Link form. This is a UX nicety — the
// backend's Zod validators are the security boundary (RULE-PORTAL-04). Field
// shapes mirror docs/integration-design.md §3 Seam 1.

const httpsUrl = z
  .string()
  .trim()
  .url('Must be a valid URL')
  .refine((v) => v.startsWith('https://'), 'Must be an HTTPS URL');

const optionalHttpsUrl = z
  .union([httpsUrl, z.literal('')])
  .optional()
  .transform((v) => (v === '' ? undefined : v));

const optionalText = z
  .string()
  .trim()
  .max(255)
  .optional()
  .transform((v) => (v === '' || v == null ? undefined : v));

const piiGuarded = optionalText.refine(
  (v) => !v || !/@|^\+?\d[\d\s-]{6,}$/.test(v),
  'Use a subscriber reference, not a raw phone/email (RULE-02)',
);

const slugField = z
  .string()
  .trim()
  .regex(new RegExp(`^[A-Za-z0-9]{${SLUG_LENGTH}}$`), `Slug must be ${SLUG_LENGTH} alphanumeric characters`);

const attributionWindow = z
  .number({ invalid_type_error: 'Enter a number' })
  .int('Whole hours only')
  .min(1, 'At least 1 hour')
  .max(2160, 'At most 2160 hours (90 days)');

const expiresAt = z
  .string()
  .optional()
  .transform((v) => (v === '' || v == null ? undefined : v))
  .refine(
    (v) => v == null || new Date(v).getTime() > Date.now(),
    'Expiry must be in the future',
  );

// Three discriminated branches by Purpose. Each branch enforces its own
// required fields; everything else is optional.

const linkNameField = z
  .string()
  .trim()
  .min(1, 'Give the link a name')
  .max(120, 'Keep it under 120 characters');

const baseFields = {
  linkName: linkNameField,
  slug: slugField,
  campaignId: optionalText,
  campaignName: optionalText,
  contactRef: piiGuarded,
  attributionWindowHours: attributionWindow,
  expiresAt,
};

const websiteBranch = z.object({
  ...baseFields,
  purpose: z.literal('website'),
  websiteUrl: httpsUrl,
});

// Store URLs + fallback are optional — they default to the tenant's App Profile
// (set once). The backend resolves the final destination from the profile.
const appDownloadBranch = z.object({
  ...baseFields,
  purpose: z.literal('app_download'),
  androidAppStoreUrl: optionalHttpsUrl,
  iosAppStoreUrl: optionalHttpsUrl,
  ipadAppStoreUrl: optionalHttpsUrl,
  huaweiAppGalleryUrl: optionalHttpsUrl,
  webFallbackUrl: optionalHttpsUrl,
  androidPackageName: optionalText,
  appScheme: optionalText,
});

const deepLinkBranch = z
  .object({
    ...baseFields,
    purpose: z.literal('deep_link'),
    androidDeepLink: optionalHttpsUrl,
    iosDeepLink: optionalHttpsUrl,
    androidAppStoreUrl: optionalHttpsUrl,
    iosAppStoreUrl: optionalHttpsUrl,
    androidPackageName: optionalText,
    appScheme: optionalText,
  })
  .refine((v) => Boolean(v.androidDeepLink || v.iosDeepLink), {
    path: ['androidDeepLink'],
    message: 'Give at least one deep link (Android or iOS)',
  });

const whatsappBranch = z.object({
  ...baseFields,
  purpose: z.literal('whatsapp'),
  whatsappNumber: z
    .string()
    .trim()
    .min(5, 'Enter a number with country code')
    .refine((v) => v.replace(/\D/g, '').length >= 5, 'Enter a valid number (with country code)'),
  whatsappMessage: optionalText,
});

// Plain union (not discriminatedUnion) so the app_download / deep_link
// branches can carry their object-level cross-field refines.
export const createLinkSchema = z.union([websiteBranch, appDownloadBranch, deepLinkBranch, whatsappBranch]);

// Per-purpose schemas exposed so the form can validate against the SELECTED
// branch only — z.union collapses branch failures into a single root-level
// "Invalid input" message that strips per-field errors, hiding the real
// reason from the UI. Validating against the chosen branch keeps Zod's
// field-level errors intact for inline FormErrorMessage rendering.
export const branchByPurpose = {
  website: websiteBranch,
  app_download: appDownloadBranch,
  deep_link: deepLinkBranch,
  whatsapp: whatsappBranch,
} as const;

export type CreateLinkFormValues = z.infer<typeof createLinkSchema>;

export function flattenZodError(err: z.ZodError): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of err.issues) {
    const key = issue.path.join('.') || '_root';
    if (!out[key]) out[key] = issue.message;
  }
  return out;
}

export { PURPOSES };
